'use client';

import {
  AddressPoint,
  useAddressStore,
} from '@/entities/address/model/use-address-store';
import EditIcon from '@/shared/assets/icons/edit';
import LocationCyanIcon from '@/shared/assets/icons/location-cyan';
import BottomSheet from '@/shared/ui/bottom-sheet';
import { toaster } from '@/shared/ui/toaster';
import ConfirmModal from '@/widgets/confirm-modal';
import TrashRed from '@/widgets/map/Delivery/icons/TrashRed';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { FC, useState } from 'react';

interface IProps {
  isOpen: boolean;
  isModal?: boolean;
  onClose: () => void;
}

const Points: FC<IProps> = ({ isOpen, onClose }) => {
  const points = useAddressStore((state) => state.points);
  const openMap = useAddressStore((state) => state.openMap);
  const openPoint = useAddressStore((state) => state.openPoint);
  const activatePoint = useAddressStore((state) => state.activatePoint);
  const removePoint = useAddressStore((state) => state.removePoint);

  const t = useTranslations();
  const [isDelete, setIsDelete] = useState<string>('');

  const handleClick = () => {
    openMap();
    onClose();
  };

  const handleSelected = (point: AddressPoint) => {
    if (point?.location && point?.location?.[0] && point?.location?.[1]) {
      activatePoint(point.id);
    }
  };

  if (points?.length > 0)
    return (
      <>
        <BottomSheet isOpen={isOpen} onClose={onClose}>
          <Text color={'text'} px={4} fontSize="xl" fontWeight="bold">
            {t('addresses')}
          </Text>
          <Box>
            {points?.map((point, index) => (
              <Box key={index} py={2} borderBottom="1px solid #E5E5E5">
                <Flex align="center" justifyContent="space-between" p={4}>
                  <Flex
                    pr={4}
                    flex={1}
                    align="center"
                    gap={4}
                    onClick={() => {
                      handleSelected(point);
                      onClose();
                    }}
                  >
                    <Box w="24px" h="24px">
                      <LocationCyanIcon color="var(--primary-default)" />
                    </Box>
                    <Box>
                      <Text color={'text'} fontWeight="bold" fontSize="md">
                        {point.name || point.address}
                      </Text>
                      <Text
                        wordBreak="break-all"
                        color={'text.muted'}
                        fontSize="sm"
                        lineClamp={1}
                      >
                        {point.name ? point.address : point.city}
                      </Text>
                    </Box>
                  </Flex>
                  <Box display="flex">
                    {point.name && (
                      <Button
                        w="24px"
                        h="24px"
                        p={0}
                        bg="transparent"
                        onClick={() => openPoint(point)}
                      >
                        <EditIcon />
                      </Button>
                    )}
                    <Button
                      w="24px"
                      h="24px"
                      p={0}
                      bg="transparent"
                      onClick={() => {
                        setIsDelete(point.id);
                      }}
                    >
                      <TrashRed />
                    </Button>
                  </Box>
                </Flex>
              </Box>
            ))}
          </Box>
          <Box px={4} mb={4}>
            <Button
              onClick={handleClick}
              w={'100%'}
              size="xl"
              mt={4}
              rounded={16}
              h={'56px'}
              bg="primary"
              color="primary.text"
              _hover={{
                bg: 'primary.hover',
              }}
            >
              {t('new_address')}
            </Button>
          </Box>
        </BottomSheet>
        <ConfirmModal
          isWarning={true}
          isOpen={Boolean(isDelete)}
          onConfirm={() => {
            toaster.create({
              title: t('address_successfully_deleted'),
              type: 'success',
            });
            removePoint(isDelete);
            setIsDelete('');
          }}
          title={t('remove_address')}
          onClose={() => setIsDelete('')}
          description={''}
        />
      </>
    );

  return null;
};

export default Points;
